/**
 * Local copy of the SDK masking rules for the Privacy Preview page.
 *
 * The SDK (`@backstory/browser`) implements these in packages/sdk/src/record/privacy.ts
 * but does not export them from its public entry point yet. This module mirrors the
 * behaviour so the preview matches what the recorder will do. Requested: export
 * `maskText`, `maskPII`, `classify` and the detectors from the SDK so this file can
 * become a re-export (see README "API additions requested").
 */
import type { PrivacyMode } from "@backstory/protocol";

export type PiiKind = "email" | "phone" | "card" | "ssn" | "iban";

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE = /(?:\+?\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g;
const CARD = /\b(?:\d[ -]?){13,19}\b/g;
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const IBAN = /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g;

export function luhnValid(digits: string): boolean {
  const d = digits.replace(/\D/g, "");
  if (d.length < 13) return false;
  let sum = 0;
  let dbl = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let n = Number(d[i]);
    if (dbl) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    dbl = !dbl;
  }
  return sum % 10 === 0;
}

/** Replace every non-whitespace char with a bullet, preserving length and layout. */
export function maskString(s: string): string {
  return s.replace(/\S/g, "•");
}

export interface PiiHit {
  kind: PiiKind;
  start: number;
  end: number;
  text: string;
}

export function detectPII(text: string): PiiHit[] {
  const hits: PiiHit[] = [];
  const push = (kind: PiiKind, re: RegExp, filter?: (m: string) => boolean) => {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (filter && !filter(m[0])) continue;
      hits.push({ kind, start: m.index, end: m.index + m[0].length, text: m[0] });
    }
  };
  push("email", EMAIL);
  push("card", CARD, luhnValid);
  push("ssn", SSN);
  push("iban", IBAN);
  push("phone", PHONE, (m) => m.replace(/\D/g, "").length >= 10);
  // Remove overlaps (keep the first found, prefer longer)
  hits.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: PiiHit[] = [];
  for (const h of hits) {
    const last = out[out.length - 1];
    if (last && h.start < last.end) continue;
    out.push(h);
  }
  return out;
}

export interface MaskedSegment {
  text: string;
  masked: boolean;
  kind?: PiiKind | "strict";
}

/** Strict hides all text. Other modes keep page text but still hide card numbers. */
export function maskTextSegments(text: string, mode: PrivacyMode, forceMask = false): MaskedSegment[] {
  if (mode === "strict" || forceMask) return [{ text: maskString(text), masked: true, kind: "strict" }];
  const hits = detectPII(text).filter((h) => h.kind === "card");
  if (!hits.length) return [{ text, masked: false }];
  const segs: MaskedSegment[] = [];
  let pos = 0;
  for (const h of hits) {
    if (h.start > pos) segs.push({ text: text.slice(pos, h.start), masked: false });
    segs.push({ text: maskString(h.text), masked: true, kind: h.kind });
    pos = h.end;
  }
  if (pos < text.length) segs.push({ text: text.slice(pos), masked: false });
  return segs;
}

export type Classification = "none" | "mask" | "block";

export function classify(el: Element): Classification {
  if (el.hasAttribute("data-backstory-block")) return "block";
  if (el.hasAttribute("data-backstory-mask")) return "mask";
  return "none";
}

const SENSITIVE_NAME = /cvv|cvc|csc|card.?num|cc.?num|cc.?exp|card.?exp|security.?code|one.?time|otp/i;

/** Passwords, card fields, and one-time codes are hidden in every mode. */
export function isAlwaysMasked(el: Element): boolean {
  const type = (el.getAttribute("type") || "").toLowerCase();
  if (type === "password") return true;
  const ac = (el.getAttribute("autocomplete") || "").toLowerCase().trim();
  if (/^(cc-|new-password|current-password|one-time-code)/.test(ac)) return true;
  return SENSITIVE_NAME.test(el.getAttribute("name") || "") || SENSITIVE_NAME.test(el.id || "");
}

export function shouldMaskInput(el: Element, mode: PrivacyMode, ancestorMasked: boolean): boolean {
  const tag = el.tagName.toLowerCase();
  if (!["input", "textarea", "select"].includes(tag) && !(el as HTMLElement).isContentEditable) return false;
  if (isAlwaysMasked(el) || ancestorMasked) return true;
  if (mode === "permissive") return false;
  const type = (el.getAttribute("type") || "text").toLowerCase();
  return !["search", "submit", "button", "reset", "checkbox", "radio", "range", "color"].includes(type);
}

export interface PreviewResult {
  html: string;
  stats: { textNodes: number; maskedText: number; inputs: number; maskedInputs: number; images: number; blockedImages: number; hits: Record<PiiKind, number> };
}

/**
 * Apply masking to a DOM tree in place (a detached document) and produce
 * annotated HTML where masked text is wrapped in <mark> for the preview pane.
 */
export function maskDocumentForPreview(doc: Document, mode: PrivacyMode, allowUnmask = false): PreviewResult {
  const stats: PreviewResult["stats"] = { textNodes: 0, maskedText: 0, inputs: 0, maskedInputs: 0, images: 0, blockedImages: 0, hits: { email: 0, phone: 0, card: 0, ssn: 0, iban: 0 } };
  const root = doc.body ?? doc.documentElement;
  if (!root) return { html: "", stats };

  const walk = (node: Node, ancestorMasked: boolean, ancestorUnmasked: boolean) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === "script" || tag === "noscript" || tag === "style") {
        el.remove();
        return;
      }
      const cls = classify(el);
      if (cls === "block") {
        const box = doc.createElement("div");
        box.setAttribute("data-backstory-blocked", "");
        box.textContent = `[blocked ${tag}]`;
        el.replaceWith(box);
        return;
      }
      const unmasked = ancestorUnmasked || (allowUnmask && el.hasAttribute("data-backstory-unmask"));
      const masked = !unmasked && (ancestorMasked || cls === "mask");
      if (tag === "img" || tag === "picture" || tag === "video") {
        stats.images++;
        if (mode === "strict" && !unmasked) {
          stats.blockedImages++;
          el.removeAttribute("src");
          el.removeAttribute("srcset");
          el.setAttribute("data-backstory-blocked", "");
          el.setAttribute("alt", "[image blocked]");
        }
      }
      if (tag === "input" || tag === "textarea" || tag === "select" || (el as HTMLElement).isContentEditable) {
        stats.inputs++;
        if (shouldMaskInput(el, mode, masked)) {
          stats.maskedInputs++;
          const v = el.getAttribute("value");
          if (v) el.setAttribute("value", maskString(v));
          const ph = el.getAttribute("placeholder");
          if (ph) el.setAttribute("placeholder", maskString(ph));
          el.setAttribute("data-backstory-masked-input", "");
        } else {
          const v = el.getAttribute("value");
          if (v) el.setAttribute("value", maskTextSegments(v, mode).map((seg) => seg.text).join(""));
        }
      }
      for (const child of Array.from(el.childNodes)) walk(child, masked, unmasked);
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? "";
      if (!text.trim()) return;
      stats.textNodes++;
      const segs = maskTextSegments(text, mode, ancestorMasked && !ancestorUnmasked);
      if (segs.every((s) => !s.masked)) return;
      stats.maskedText++;
      const frag = doc.createDocumentFragment();
      for (const s of segs) {
        if (s.masked) {
          if (s.kind && s.kind !== "strict") stats.hits[s.kind]++;
          const mark = doc.createElement("mark");
          mark.className = s.kind === "strict" ? "masked" : "pii";
          mark.setAttribute("data-kind", s.kind ?? "");
          mark.textContent = s.text;
          frag.appendChild(mark);
        } else frag.appendChild(doc.createTextNode(s.text));
      }
      (node as ChildNode).replaceWith(frag);
    }
  };
  for (const child of Array.from(root.childNodes)) walk(child, false, false);
  return { html: root.innerHTML, stats };
}

/** Email addresses, phone numbers, and similar details that privacy off shows. */
export function findHits(doc: Document, mode: PrivacyMode): PiiHit[] {
  if (mode === "strict") return [];
  const hits: PiiHit[] = [];
  const walker = doc.createTreeWalker(doc.body ?? doc.documentElement, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) hits.push(...detectPII(n.nodeValue ?? "").filter((h) => h.kind !== "card"));
  return hits;
}
